import qs from 'qs';
import forEach from 'lodash/forEach';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import find from 'lodash/find';
import property from 'lodash/property';
import keys from 'lodash/keys';
import get from 'lodash/get';
import head from 'lodash/head';
import uniq from 'lodash/uniq';
import isNull from 'lodash/isNull';

import moment from 'moment';

import { enum as affiliateLevels } from '../../../../../enums/affiliate-levels';

const isRocket = process.env.CPA_ENV === 'rocket';

const getRequestQuery = (
	state,
	activeBalance = 'usd',
	externalQuery,
	getFullFilters = false,
	isMaster = false,
	masterSwitchConfig
) => {

	const { query, fullAppliedFilters, calendar = {} } = cloneDeep(state);

	if (getFullFilters) {
		query['filters'] = fullAppliedFilters ? fullAppliedFilters : null;
	}

	if (externalQuery && externalQuery.filters) {
		const externalFilters = {};
		const externalFiltersKeys = Object.keys(externalQuery.filters);

		externalFiltersKeys.length &&
			externalFiltersKeys.forEach(key => {
				externalFilters[key] = externalQuery.filters[key].map(
					item => (getFullFilters ? { id: item.id, text: item.text } : item.id)
				);
			});

		if (!isEmpty(externalFilters)) {
			query['filters'] = externalFilters;
		}
	}

	if (calendar['value'] && calendar['value'].start && calendar['value'].end) {
		let dateStart = moment(calendar['value'].start).hours(0).minutes(0).seconds(0).milliseconds(0);
		let dateEnd = moment(calendar['value'].end).hours(23).minutes(59).seconds(59).milliseconds(999);
		query['date_range'] = {
			start: dateStart.format('YYYY-MM-DD HH:mm:ss.SSS'),
			end: dateEnd.format('YYYY-MM-DD HH:mm:ss.SSS')
		};
	}

	query['currency'] = query.currency ? query.currency : activeBalance;

	if (isMaster) {
		let masterDisplayStatsSetting;
		if (query.filters && query.filters.affiliates && query.filters.affiliates.length) {
			masterDisplayStatsSetting = find(masterSwitchConfig, { id: 'children_only' });
		} else {
			masterDisplayStatsSetting = find(masterSwitchConfig, { active: true });
		}

		query['master_account_display_stats_setting'] = masterDisplayStatsSetting.id;
	}

	return {
		...query,
		...omit(externalQuery, 'filters')
	};
};

const makeStatisticQuery = (queryObject = {}) => {
	let query = {
		currency: 'usd',
		groups: ['date'],
		table: [],
		conditions: []
	};

	return qs.stringify({
		...query,
		...queryObject
	});
};

const makeQueryBySettings = settings => {
	const res = {};

	forEach(settings, (value, key) => {
		if (key === 'groups') {
			res['groups'] = value.slice(0, 2);
		} else {
			res[key] = value;
		}
	});

	return makeStatisticQuery(res);
};

const makeFilterSettings = (settings, state) => {
	const { filters, columnControl } = state;

	let res = {};
	let hiddenColumns = [];
	let columnConditions = [];
	let selectedFilters = {};

	forEach(settings, (value, key) => {
		if (key != 'date_range') {
			res[key] = value;
		}
	});

	if (filters) {
		selectedFilters = mapQueryFilters(filters, false);
	}

	if (columnControl) {
		hiddenColumns = getColumnsForDisable(columnControl);
		columnConditions = getColumnControlFilters(columnControl);
	}

	return { ...res, filters: selectedFilters, table: hiddenColumns, conditions: columnConditions };
};

function getDateRangeFromCalendar(calendar) {
	if (calendar['value'] && calendar['value'].start && calendar['value'].end) {
		let dateStart = moment(calendar['value'].start).hours(0).minutes(0).seconds(0).milliseconds(0);
		let dateEnd = moment(calendar['value'].end).hours(23).minutes(59).seconds(59).milliseconds(999);
		return {
			start: dateStart.format('YYYY-MM-DD HH:mm:ss.SSS'),
			end: dateEnd.format('YYYY-MM-DD HH:mm:ss.SSS')
		};
	}
}

const getColumnsForDisable = columnControl => {
	let res = [];

	if (columnControl) {
		Object.keys(columnControl).forEach(parentColumn => {
			const formData = columnControl[parentColumn]['form'];
			if (formData) {
				return Object.keys(formData).forEach(columnName => {
					if (!formData[columnName]['isColumnVisible']) {
						res.push(columnName);
					}
				});
			}
		});
	}

	return res;
};

const getColumnControlFilters = columnControl => {
	let columnControlFilters = [];

	if (columnControl) {
		Object.keys(columnControl).forEach(parentColumn => {
			const formData = columnControl[parentColumn]['form'];
			if (formData) {
				Object.keys(formData).forEach(columnName => {
					if (
						!!formData[columnName]['conditionsType'] &&
						!!formData[columnName]['conditionsValue'] &&
						formData[columnName]['isColumnVisible']
					) {
						columnControlFilters.push({
							columnName,
							conditionsType: formData[columnName]['conditionsType'],
							conditionsValue: formData[columnName]['conditionsValue']
						});
					}
				});
			}
		});
	}

	return columnControlFilters;
};

const mapQueryFilters = (
	filters,
	returnIds = true,
	returnFilterTitle = false,
	returnChildrenHighlight = false
) => {
	const selectedFilters = {};

	filters.forEach(filter => {
		if (filter.hasAppliedItems) {
			for (let key in filter.suggest) {
				const suggest = filter.suggest[key];
				selectedFilters[key] = returnIds ? suggest.appliedItems.map(item => item.id) : cloneDeep(suggest.appliedItems);
				if (returnChildrenHighlight) {
					selectedFilters[key] = selectedFilters[key].map(obj => ({
						...obj,
						shouldHighlight: filter.shouldHighlightChildren
					}));
				}
				if (returnFilterTitle) {
					selectedFilters[key] = selectedFilters[key].map(obj => ({
						...obj,
						filterTitle: filter.title
					}));
				}
			}
		}
	});

	return selectedFilters;
};

const getFilterAndSuggest = (itemId, filterTitle, state) => {
	let [filter] = state.filter(f => f.title === filterTitle);
	let suggestToUpdate;
	let filterToUpdate;

	filter &&
		Object.keys(filter.suggest).forEach(suggestName => {
			let appliedItemMatch = find(filter.suggest[suggestName].appliedItems, { id: itemId });
			if (appliedItemMatch) {
				suggestToUpdate = filter.suggest[suggestName];
				filterToUpdate = filter;
			}
		});

	return [filterToUpdate, suggestToUpdate];
};

const getCurrentCurrency = (state, namespace) => {
	if (state[namespace].query.currency) {
		return state[namespace].query.currency;
	}
	if (isRocket) {
		return 'usd';
	}
	return state.app.userData && state.app.userData.activeBalance;
};

const getChainsQuery = state => ({
	filters: state.filters
		.map(property('suggest'))
		.reduce((acc, item) => {
			const key = head(keys(item));
			acc[key] = get(item, [key, 'selectedItems']).map(property('id'));
			return acc;
		}, {})
});

const createKey = (offerId, chainIds) => `${offerId}__${chainIds.join(',')}`;

const extractDistinctCurrencies = (...payouts) => uniq(payouts
	.reduce((acc, items) => items.map(property('currency')).concat(acc), [])
	.filter(Boolean));

const payoutsCreator =  (indPayouts, basePayouts) => {
	const currencies = extractDistinctCurrencies(indPayouts, basePayouts);
	return currencies.reduce((acc, currency) => {
		const indPayout = find(indPayouts, { currency });
		const basePayout = find(basePayouts, { currency });
		const indAmount = get(indPayout, ['amount'], 0);
		const baseAmount = get(basePayout, ['amount'], 0);
		acc[currency] = {
			ind: indAmount - baseAmount === 0 ? 0 : indAmount,
			base: baseAmount,
			isBoosted: !isNull(get(indPayout, 'boost_till'))
		};
		return acc;
	}, {});
};

const isAccessGranted = offer => get(offer, 'available', false);
const isWaitingForActivation = offer => offer && !offer.available
	&& offer.offer_access_thread_id && !offer.offer_access_thread_closed_at;
const isAccessRejected = offer => offer && !offer.available
	&& offer.offer_access_thread_id && Boolean(offer.offer_access_thread_closed_at);
const isOfferConnectNeeded = (offer, userLevel) => {
	return !isAccessGranted(offer)
		&& !offer.offer_access_thread_id
		&& userLevel === affiliateLevels.average;
};
const isAccessRequestNeeded = (offer, userLevel) => {
	return !isAccessGranted(offer)
		&& !offer.offer_access_thread_id
		&& userLevel === affiliateLevels.noob;
};

const toggleInitialCurrency = (state, action) => ({
	...state,
	data: [],
	query: {
		...state.query,
		currency: action.payload.data.currency
	}
});

const initCurrencies = (state, action) => ({
	...state,
	listOfCurrencies: action.payload
});
const initDefaultCurrenciesAction = (getState, dispatch, type, namespace) => {
	if (!getState()[namespace].listOfCurrencies.length) {
		dispatch({
			type,
			payload: getState().app.userData.balances.map(item => ({
				id: item.currency,
				text: item.currency
			}))
		});
	}
};

export {
	getRequestQuery,
	makeStatisticQuery,
	makeQueryBySettings,
	makeFilterSettings,
	mapQueryFilters,
	getFilterAndSuggest,
	getCurrentCurrency,
	getChainsQuery,
	createKey,
	payoutsCreator,
	isAccessGranted,
	getDateRangeFromCalendar,
	isWaitingForActivation,
	isAccessRejected,
	isOfferConnectNeeded,
	isAccessRequestNeeded,
	toggleInitialCurrency,
	initCurrencies,
	initDefaultCurrenciesAction
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/statistics/shared/utils.js